import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';
import { invoiceDetailQueryKey, type InvoiceLineRow } from '@/hooks/useInvoices';
import { patientAppointmentsQueryKey } from '@/hooks/usePatientAppointments';
import { appointmentDetailQueryKey } from '@/hooks/useAppointments';
import { preferAppointmentInvoiceLink } from '@/lib/appointmentSettlement';

export type AppointmentStatus = Database['public']['Enums']['appointment_status'];

export interface UninvoicedAppointment {
  id: string;
  starts_at: string;
  ends_at: string;
  status: AppointmentStatus;
  service_id: string | null;
  service_name: string;
  price_cents: number;
  vat_rate_percent: number;
  clinician_name: string | null;
}

export interface AppointmentInvoiceLink {
  appointment_id: string;
  invoice_id: string;
  invoice_number: string | null;
  invoice_status: Database['public']['Enums']['invoice_status'];
  payment_method: string | null;
  total_cents: number | null;
  total_paid_cents: number | null;
  currency: string | null;
  last_sent_at: string | null;
  created_at: string | null;
  paid_at?: string | null;
  kind?: string | null;
}

export const appointmentInvoiceLinkQueryKey = (appointmentId: string | null | undefined) =>
  ['appointments', 'invoice-link', appointmentId ?? null] as const;

export const uninvoicedAppointmentsKey = (
  patientId: string | null | undefined,
  clinicId: string | null | undefined,
) => ['invoices', 'uninvoiced-appointments', patientId ?? null, clinicId ?? null] as const;

export function useUninvoicedAppointments(
  patientId: string | null | undefined,
  clinicId: string | null | undefined,
) {
  return useQuery({
    queryKey: uninvoicedAppointmentsKey(patientId, clinicId),
    enabled: Boolean(patientId) && Boolean(clinicId),
    queryFn: async (): Promise<UninvoicedAppointment[]> => {
      if (!patientId || !clinicId) return [];
      const { data, error } = await supabase.rpc('list_uninvoiced_appointments_for_patient', {
        p_patient_id: patientId,
        p_clinic_id: clinicId,
      });
      if (error) throw error;
      return (data ?? []) as UninvoicedAppointment[];
    },
    staleTime: 10_000,
  });
}

export function useAddAppointmentInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      invoice_id: string;
      appointment_id: string;
      patient_id: string;
      clinic_id: string;
    }): Promise<InvoiceLineRow> => {
      const { data, error } = await supabase.rpc('add_appointment_invoice_line', {
        p_invoice_id: input.invoice_id,
        p_appointment_id: input.appointment_id,
      });
      if (error) throw error;
      return data as unknown as InvoiceLineRow;
    },
    onSuccess: (row, input) => {
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.invoice_id) });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      qc.invalidateQueries({
        queryKey: uninvoicedAppointmentsKey(input.patient_id, input.clinic_id),
      });
      qc.invalidateQueries({ queryKey: patientAppointmentsQueryKey(input.patient_id) });
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(input.appointment_id) });
    },
  });
}

export function buildAppointmentInvoiceHref(input: {
  invoiceId?: string | null;
  patientId: string;
  clinicId: string;
  appointmentId: string;
  returnTo: string;
}): string {
  if (input.invoiceId) return `/invoices/${input.invoiceId}`;
  const params = new URLSearchParams({
    patient_id: input.patientId,
    clinic_id: input.clinicId,
    appointment_id: input.appointmentId,
    return_to: input.returnTo,
  });
  return `/invoices/new?${params.toString()}`;
}

export function useAppointmentInvoiceLink(appointmentId: string | null | undefined) {
  return useQuery({
    queryKey: appointmentInvoiceLinkQueryKey(appointmentId),
    enabled: Boolean(appointmentId),
    staleTime: 10_000,
    queryFn: async (): Promise<AppointmentInvoiceLink | null> => {
      if (!appointmentId) return null;
      const map = await fetchAppointmentInvoiceLinks([appointmentId]);
      return map.get(appointmentId) ?? null;
    },
  });
}

type InvoiceLinkRow = {
  id: string;
  invoice_number: string | null;
  status: Database['public']['Enums']['invoice_status'];
  payment_method: string | null;
  total_cents: number | null;
  total_paid_cents: number | null;
  currency: string | null;
  last_sent_at: string | null;
  created_at: string | null;
  paid_at: string | null;
  kind: string | null;
};

export async function fetchAppointmentInvoiceLinks(
  appointmentIds: string[],
): Promise<Map<string, AppointmentInvoiceLink>> {
  if (appointmentIds.length === 0) return new Map();

  const [lineRes, directRes] = await Promise.all([
    supabase
      .from('invoice_line_items')
      .select(
        'source_id, invoice_id, invoices!inner(id, invoice_number, status, kind, payment_method, total_cents, total_paid_cents, currency, last_sent_at, paid_at, created_at)',
      )
      .eq('source', 'appointment')
      .in('source_id', appointmentIds),
    supabase
      .from('invoices')
      .select(
        'id, invoice_number, status, kind, payment_method, total_cents, total_paid_cents, currency, appointment_id, last_sent_at, paid_at, created_at',
      )
      .in('appointment_id', appointmentIds),
  ]);

  if (lineRes.error) throw lineRes.error;
  if (directRes.error) throw directRes.error;

  const map = new Map<string, AppointmentInvoiceLink>();
  const consider = (sourceId: string, next: AppointmentInvoiceLink) => {
    map.set(sourceId, preferAppointmentInvoiceLink(map.get(sourceId), next));
  };

  for (const row of lineRes.data ?? []) {
    const sourceId = row.source_id as string | null;
    if (!sourceId) continue;
    const inv = row.invoices as unknown as InvoiceLinkRow;
    if (inv.kind === 'credit_note') continue;
    consider(sourceId, {
      appointment_id: sourceId,
      invoice_id: inv.id,
      invoice_number: inv.invoice_number,
      invoice_status: inv.status,
      payment_method: inv.payment_method,
      total_cents: inv.total_cents,
      total_paid_cents: inv.total_paid_cents,
      currency: inv.currency,
      last_sent_at: inv.last_sent_at,
      created_at: inv.created_at,
      paid_at: inv.paid_at,
      kind: inv.kind,
    });
  }

  for (const inv of directRes.data ?? []) {
    if (!inv.appointment_id || inv.kind === 'credit_note') continue;
    consider(inv.appointment_id, {
      appointment_id: inv.appointment_id,
      invoice_id: inv.id,
      invoice_number: inv.invoice_number,
      invoice_status: inv.status,
      payment_method: inv.payment_method,
      total_cents: inv.total_cents,
      total_paid_cents: inv.total_paid_cents,
      currency: inv.currency,
      last_sent_at: inv.last_sent_at,
      created_at: inv.created_at,
      paid_at: inv.paid_at,
      kind: inv.kind,
    });
  }
  return map;
}
